import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createRecordingController, stopAllTracks } from '../lib/recording';
import { processVideoFile, processAudioFile } from '../lib/assetProcessing';
import audioPlaceholder from '../../../assets/images/section-menu/audio-placeholder.png';
import {
  setIsRecording,
  setIsCameraEnabled,
  setIsScreenEnabled,
  setIsAudioEnabled,
  setIsTimerRunning,
  setTimerValue,
  setRecordingTime,
  setIsScreenRecordReminderVisible,
  setIsCameraRecordingPaused,
} from '../store/slices/recordingSlice';
import {
  setMenuVisible,
} from '../store/slices/editorUISlice';
import {
  addAsset,
} from '../store/slices/assetsSlice';
import {
  addTimelineItem,
} from '../store/slices/timelineSlice';

export function useRecording({
  currentTime,
  selectedItemId,
  currentItem,
  dispatch,
}) {
  const mediaRecorderRef = useRef();
  const streamRef = useRef();
  const videoRef = useRef();
  const [cameraFacingMode, setCameraFacingMode] = useState('user');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const isMobileOrTabletDevice =
    /android|iphone|ipad|ipod/i.test(navigator.userAgent) || navigator.maxTouchPoints > 1;

  // Recording Redux state
  const isRecording = useSelector((state) => state.recording.isRecording);
  const isCameraEnabled = useSelector((state) => state.recording.isCameraEnabled);
  const isScreenEnabled = useSelector((state) => state.recording.isScreenEnabled);
  const isAudioEnabled = useSelector((state) => state.recording.isAudioEnabled);
  const isScreenRecordReminderVisible = useSelector((state) => state.recording.isScreenRecordReminderVisible);
  const isTimerRunning = useSelector((state) => state.recording.isTimerRunning);
  const timerValue = useSelector((state) => state.recording.timerValue);
  const recordingTime = useSelector((state) => state.recording.recordingTime);
  const isCameraRecordingPaused = useSelector((state) => state.recording.isCameraRecordingPaused);

  // UA flags (match legacy checks)
  const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Build recording controller
  const recorder = createRecordingController({
    mediaRecorderRef,
    streamRef,
    videoRef,
    isFirefox,
    isSafari,
  });

  // Cleanup active tracks on unmount
  useEffect(() => () => {
    stopAllTracks();
  }, []);

  const detectAvailableCameras = useCallback(async () => {
    if (!navigator?.mediaDevices?.enumerateDevices) {
      setHasMultipleCameras(false);
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((device) => device.kind === 'videoinput');
      const uniqueDeviceIds = new Set(videoInputs.map((device) => device.deviceId).filter(Boolean));
      const count = uniqueDeviceIds.size || videoInputs.length;
      setHasMultipleCameras(isMobileOrTabletDevice && count > 1);
    } catch (e) {
      setHasMultipleCameras(false);
    }
  }, [isMobileOrTabletDevice]);

  useEffect(() => {
    detectAvailableCameras();
  }, [detectAvailableCameras]);

  useEffect(() => {
    if (isCameraEnabled) {
      detectAvailableCameras();
    }
  }, [detectAvailableCameras, isCameraEnabled]);

  useEffect(() => {
    if (!navigator?.mediaDevices?.addEventListener) return () => {};

    const onDeviceChange = () => {
      detectAvailableCameras();
    };

    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
    };
  }, [detectAvailableCameras]);

  // Sync stream to video element when both exist (safety sync after recording.js binding)
  useEffect(() => {
    if ((isCameraEnabled || isScreenEnabled) && videoRef.current && streamRef.current && !videoRef.current.srcObject) {
      try {
        // eslint-disable-next-line no-param-reassign
        videoRef.current.srcObject = streamRef.current;
        // eslint-disable-next-line no-param-reassign
        videoRef.current.muted = true;
        // eslint-disable-next-line no-param-reassign
        videoRef.current.playsInline = true;
        videoRef.current.play().catch(() => {});
      } catch (e) { /* noop */ }
    }
  }, [isCameraEnabled, isScreenEnabled]);

  // Recording timer ticker
  useEffect(() => {
    let t;
    if (isRecording) {
      t = setInterval(() => {
        dispatch(setRecordingTime(recordingTime + 1));
      }, 1000);
    }
    return () => {
      if (t) clearInterval(t);
    };
  }, [isRecording, recordingTime, dispatch]);

  const startCountdownThenStart = () => {
    // Reset timer for a fresh recording session
    dispatch(setRecordingTime(0));
    dispatch(setIsTimerRunning(true));
    dispatch(setTimerValue(3));
    setTimeout(() => dispatch(setTimerValue(2)), 1000);
    setTimeout(() => dispatch(setTimerValue(1)), 2000);
    setTimeout(() => {
      if (mediaRecorderRef.current) {
        try {
          // Set recording flag BEFORE starting (matches legacy behavior)
          dispatch(setIsRecording(true));
          mediaRecorderRef.current.start();
        } catch (e) { /* noop */ }
      }
      dispatch(setIsTimerRunning(false));
      dispatch(setTimerValue(3));
    }, 3000);
  };

  const cancelArming = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      dispatch(setIsRecording(false));
      dispatch(setIsCameraRecordingPaused(false));
      dispatch(setRecordingTime(0));
      dispatch(setIsCameraEnabled(false));
      dispatch(setIsScreenEnabled(false));
      dispatch(setIsAudioEnabled(false));
      stopAllTracks();
      try { if (videoRef.current) videoRef.current.srcObject = null; } catch (e) { /* noop */ }
    } catch (e) { /* noop */ }
  };

  const handleRecordSelf = async () => {
    dispatch(setMenuVisible(false));
    try {
      await recorder.startCamera({
        facingMode: cameraFacingMode,
        onReady: () => {
          dispatch(setIsCameraEnabled(true));
          // Wait for explicit Start Recording
        },
        onStop: async ({ blob, durationMs }) => {
          dispatch(setIsCameraEnabled(false));
          dispatch(setIsRecording(false));
          try {
            const file = new File([blob], `recording-${Date.now()}.${isSafari ? 'mp4' : 'webm'}`, { type: blob.type });
            const asset = await processVideoFile(file);
            dispatch({ ...addAsset({ ...asset, source_type: 'upload' }), meta: { undoable: true } });
            const duration = asset.duration || Math.round((durationMs || 0) / 1000);
            dispatch({
              ...addTimelineItem({
                assetId: asset.id,
                duration,
                currentTime,
                anchorItemId: selectedItemId || currentItem?.id,
              }),
              meta: { undoable: true },
            });
          } catch (e) { /* noop */ }
        },
        onError: () => {
          dispatch(setIsCameraEnabled(false));
        },
      });
    } catch (e) { /* noop */ }
  };

  const handleSwitchCamera = useCallback(async () => {
    if (!isCameraEnabled || isRecording || isTimerRunning) return;

    const nextFacingMode = cameraFacingMode === 'user' ? 'environment' : 'user';

    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (videoRef.current) {
        // eslint-disable-next-line no-param-reassign
        videoRef.current.srcObject = null;
      }
    } catch (e) { /* noop */ }

    const startCameraWithMode = async (mode) => {
      await recorder.startCamera({
        facingMode: mode,
        onReady: () => {
          setCameraFacingMode(mode);
          dispatch(setIsCameraEnabled(true));
        },
        onStop: async ({ blob, durationMs }) => {
          dispatch(setIsCameraEnabled(false));
          dispatch(setIsRecording(false));
          try {
            const file = new File([blob], `recording-${Date.now()}.${isSafari ? 'mp4' : 'webm'}`, { type: blob.type });
            const asset = await processVideoFile(file);
            dispatch({ ...addAsset({ ...asset, source_type: 'upload' }), meta: { undoable: true } });
            const duration = asset.duration || Math.round((durationMs || 0) / 1000);
            dispatch({
              ...addTimelineItem({
                assetId: asset.id,
                duration,
                currentTime,
                anchorItemId: selectedItemId || currentItem?.id,
              }),
              meta: { undoable: true },
            });
          } catch (e) { /* noop */ }
        },
        onError: () => {
          dispatch(setIsCameraEnabled(false));
        },
      });
    };

    try {
      await startCameraWithMode(nextFacingMode);
    } catch (e) {
      try {
        // Fallback: restore the previous camera if the target camera isn't available
        await startCameraWithMode(cameraFacingMode);
      } catch (err) { /* noop */ }
    }
  }, [
    cameraFacingMode,
    currentItem?.id,
    currentTime,
    dispatch,
    isCameraEnabled,
    isRecording,
    isSafari,
    isTimerRunning,
    recorder,
    selectedItemId,
  ]);

  const handleRecordAudio = async () => {
    dispatch(setMenuVisible(false));
    try {
      await recorder.startAudio({
        onReady: () => {
          dispatch(setIsAudioEnabled(true));
          // Wait for explicit Start Recording
        },
        onStop: async ({ blob, durationMs }) => {
          dispatch(setIsAudioEnabled(false));
          dispatch(setIsRecording(false));
          try {
            // MediaRecorder creates audio/webm or audio/mp4, but we need to process it
            const file = new File([blob], `audio-${Date.now()}.${isSafari ? 'mp4' : 'webm'}`, { 
              type: blob.type || (isSafari ? 'audio/mp4' : 'audio/webm')
            });
            
            // Try processAudioFile first, fallback to processVideoFile if it fails
            let audioAsset;
            try {
              audioAsset = await processAudioFile(file);
            } catch (err) {
              const fallback = await processVideoFile(file);
              audioAsset = { ...fallback, type: 'audio' };
            }
            
            dispatch({ ...addAsset({ ...audioAsset, source_type: 'upload', thumbnail_url: audioPlaceholder }), meta: { undoable: true } });
            
            // Wait a tick to ensure asset is in store before adding timeline item
            await new Promise(resolve => setTimeout(resolve, 0));
            
            const duration = audioAsset.duration || Math.round((durationMs || 0) / 1000);
            dispatch({
              ...addTimelineItem({
                assetId: audioAsset.id,
                duration,
                currentTime,
                anchorItemId: selectedItemId || currentItem?.id,
              }),
              meta: { undoable: true },
            });
          } catch (e) {
            console.error('Error in audio onStop:', e);
          }
        },
        onError: () => {
          dispatch(setIsAudioEnabled(false));
        },
      });
    } catch (e) { /* noop */ }
  };

  const confirmScreenReminder = async () => {
    dispatch(setIsScreenRecordReminderVisible(false));
    try {
      await recorder.startScreen({
        onReady: () => {
          // User already clicked Share; auto-start recording immediately
          dispatch(setRecordingTime(0));
          dispatch(setIsScreenEnabled(true));
          dispatch(setIsTimerRunning(false));
          dispatch(setIsRecording(true));
          try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
              mediaRecorderRef.current.start();
            }
          } catch (e) { /* noop */ }
        },
        onStop: async ({ blob, durationMs }) => {
          // Don't dispatch state changes here - they're already done in handleStopRecording
          try {
            const file = new File([blob], `screen-${Date.now()}.${isSafari ? 'mp4' : 'webm'}`, { type: blob.type });
            const asset = await processVideoFile(file);
            dispatch({ ...addAsset({ ...asset, source_type: 'upload' }), meta: { undoable: true } });
            const duration = asset.duration || Math.round((durationMs || 0) / 1000);
            dispatch({
              ...addTimelineItem({
                assetId: asset.id,
                duration,
                currentTime,
                anchorItemId: selectedItemId || currentItem?.id,
              }),
              meta: { undoable: true },
            });
          } catch (e) { /* noop */ }
        },
        onError: () => {
          dispatch(setIsScreenEnabled(false));
        },
      });
    } catch (e) { /* noop */ }
  };

  // Recording controls: pause/resume/stop
  const handlePauseRecording = () => {
    try {
      recorder.pause();
      dispatch(setIsCameraRecordingPaused(true));
    } catch (e) { /* noop */ }
  };

  const handleResumeRecording = () => {
    try {
      recorder.resume();
      dispatch(setIsCameraRecordingPaused(false));
    } catch (e) { /* noop */ }
  };

  const handleStopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      dispatch(setIsRecording(false));
      dispatch(setIsCameraRecordingPaused(false));
      dispatch(setRecordingTime(0));
      dispatch(setIsCameraEnabled(false));
      dispatch(setIsScreenEnabled(false));
      dispatch(setIsAudioEnabled(false));
      stopAllTracks();
      try { if (videoRef.current) videoRef.current.srcObject = null; } catch (e) { /* noop */ }
    } catch (e) { /* noop */ }
  };

  // Keyboard controls: Space => pause/resume, Esc => stop
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!isRecording || isTimerRunning) return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (isCameraRecordingPaused) {
          handleResumeRecording();
        } else {
          handlePauseRecording();
        }
      } else if (e.code === 'Escape') {
        e.preventDefault();
        handleStopRecording();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, isTimerRunning, isCameraRecordingPaused]);

  return {
    // Refs
    mediaRecorderRef,
    streamRef,
    videoRef,
    // State
    isRecording,
    isCameraEnabled,
    isScreenEnabled,
    isAudioEnabled,
    isScreenRecordReminderVisible,
    isTimerRunning,
    timerValue,
    recordingTime,
    isCameraRecordingPaused,
    hasMultipleCameras,
    // Handlers
    startCountdownThenStart,
    cancelArming,
    handleRecordSelf,
    handleSwitchCamera,
    handleRecordAudio,
    confirmScreenReminder,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
  };
}
